import { Bert } from 'meteor/themeteorchef:bert';
import { isEmpty } from 'lodash';
import methodPromise from '../../modules/method-promise-wrapper';
import {
  rpaPathProfile,
  updateAltitudesInWaypointList,
  updateDistanceTravelledInWaypointList
} from '../../modules/waypoint-utilities';
import { updateDistanceTraveled, updateMissionArea } from './mission-calculations';
import { isGroundDataDTM, getDesiredSpeed } from './mission-operations';
import getElevationFromServer from '../../modules/get-elevation';

/**
 * Updates Flight parameters and picture grid in database.
 * Updates distance flight time if waypoint list is defined.
 *
 * @async
 * @param {string} missionID - The mission ID.
 * @param {object} pictureGrid - Object that contains sidelap and overlap.
 * @param {object} flightParams - Object that contains the flight parameters.
 * @param {string} waypointReferenceAltitude - The waypoint reference altitude value.
 * @param {object} waypointList - GeoJSON Feature Collection with the waypoints.
 * @throws Error if something went wrong.
 */
export const setParams = async (
  missionID,
  pictureGrid,
  flightParams,
  waypointReferenceAltitude,
  waypointList
) => {
  try {
    await methodPromise(
      'missions.setMissionParams',
      missionID,
      flightParams,
      pictureGrid,
      waypointReferenceAltitude
    );
    Bert.alert('Mission Parameters Updated', 'success');
    if (!isEmpty(waypointList)) {
      updateDistanceTraveled(missionID, waypointList, flightParams.speed);
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Updates take off feature point.
 *
 * @async
 * @param {string} missionID - The mission ID.
 * @param {object} takeOffPoint - Feature point representing the take off point.
 */
export const setTakeOffPoint = async (missionID, takeOffPoint) => {
  try {
    await methodPromise('missions.setTakeOffPoint', missionID, takeOffPoint);
    Bert.alert('Take Off Point Set', 'success');
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Updates landing feature point.
 *
 * @async
 * @param {string} missionID - The mission ID.
 * @param {object} landingPoint - Feature point representing the landing point.
 */
export const setLandingPoint = async (missionID, landingPoint) => {
  try {
    await methodPromise('missions.setLandingPoint', missionID, landingPoint);
    Bert.alert('Landing Point Set', 'success');
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Updates mission geometry in database.
 * If mission geometry is empty then it deletes it from database.
 * Updates the total area in the mission params.
 *
 * @async
 * @param {string} missionID - The mission ID.
 * @param {object} [missionGeometry] - Feature polygon representing the mission geometry.
 */
export const setGeometry = async (missionID, missionGeometry) => {
  try {
    await methodPromise('missions.setMissionGeometry', missionID, missionGeometry);
    if (missionGeometry) {
      Bert.alert('Mission Geometry Set', 'success');
      updateMissionArea(missionID, missionGeometry);
    } else {
      Bert.alert('Mission Geometry Deleted', 'warning');
    }
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Updates the waypointList in the database.
 *
 * @param {string} missionID - The mission ID.
 * @param {object} newWayPointList - GeoJSON Feature Collection with the waypoints.
 */
export const setWaypointList = async (missionID, newWayPointList) => {
  try {
    await methodPromise('missions.editWayPointList', missionID, newWayPointList);
    Bert.alert('Mission Waypoints Updated', 'success');
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Updates the elevation profile in the database.
 *
 * @param {string} missionID - The mission ID.
 * @param {object} elevationProfile - GeoJSON Feature Collection the elevation profile
 * for that waypoint list.
 */
export const setElevationProfile = async (missionID, elevationProfile) => {
  try {
    await methodPromise('missions.editElevationProfile', missionID, elevationProfile);
    Bert.alert('Elevation Profile Saved', 'success');
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * This function is a new take on saving changes in the rpa path. The new workflow is as follows:
 * 1. Build the path profile.
 * 2. Fetch elevation data for the flight profile.
 * 3. Populate waypoint list with elevation data.
 * 4. Update distace traveled and flight time.
 * 5. Save to the database the elevation profile.
 * 6. Save to the database the new waypoint list, new distance and flight time.
 * It does this work flow when the waypoint reference is either value.
 *
 * When there is an error in the elevation fetch:
 * If the waypoint reference height is DTM an error is thrown after step 2.
 * If the waypoint reference height is TakeOffGPS then step 3 and 6 are not done.
 *
 * @param {object} mission - The mission object.
 * @param {object} newWayPointList - GeoJSON Feature Collection with the waypoints.
 * @param {boolean} isPreFlight - Flag to overwrite or not landing and take off altitudes.
 * @throws Error if mission is DTM and elevation data could not be saved.
 */
export const saveChangesInRPAPath = async (mission, newWayPointList, isPreFlight = false) => {
  try {
    const elevationProfile = rpaPathProfile(newWayPointList);
    const waypointListWithDistance = updateDistanceTravelledInWaypointList(
      newWayPointList,
      elevationProfile
    );
    let profileWithElevation = {};
    try {
      profileWithElevation = await getElevationFromServer(mission._id, elevationProfile);
    } catch (error) {
      if (isGroundDataDTM(mission)) {
        throw error;
      } else {
        profileWithElevation = {};
      }
    }
    // We only get here if height is relative to take off
    // or if mission is relative to DMT and there was no error in fetch
    const fetchError = isEmpty(profileWithElevation);
    let waypointListWithAltitudesAndDistances = {};

    let waypointListToSave = {};
    if (fetchError) {
      waypointListToSave = waypointListWithDistance;
      setElevationProfile(mission._id);
    } else {
      waypointListWithAltitudesAndDistances = updateAltitudesInWaypointList(
        waypointListWithDistance,
        profileWithElevation,
        isPreFlight
      );
      waypointListToSave = waypointListWithAltitudesAndDistances;
      setElevationProfile(mission._id, profileWithElevation);
    }
    setWaypointList(mission._id, waypointListToSave);
    updateDistanceTraveled(mission._id, waypointListToSave, getDesiredSpeed(mission));
  } catch (error) {
    throw error;
  }
};

/**
 * Saves the telemetry array of waypoints sent to the RPA.
 * If newWayPointArray is not defined then an empty array is saved.
 *
 * @async
 * @param {string} missionID - The mission ID.
 * @param {Array} [newWayPointArray=[]] - The telemetry array generated from
 * the waypoint list that can be read by the RPA.
 */
export const setWaypointTelemetryArray = async (missionID, newWayPointArray = []) => {
  try {
    await methodPromise('missions.sentWaypointTelemetryArray', missionID, newWayPointArray);
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};
/**
 * Saves the feature collection used to create the telemetry array
 * of waypoints for the RPA. If the newWaypointlist is not sent, then an empty object is saved.
 *
 * @param {string} missionID - The mission ID.
 * @param {object} [newWayPointList={}] - The waypoint list used
 * to generate the waypoint array.
 */
export const setFinalWaypointList = async (missionID, newWayPointList = {}) => {
  try {
    await methodPromise('missions.setFinalWaypointList', missionID, newWayPointList);
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Removes all the data for the flight plan in the database.
 *
 * @async
 * @param {string} missionID - The mission ID.
 */
export const clearWayPoints = async missionID => {
  try {
    await methodPromise('missions.clearWayPoints', missionID);
    Bert.alert('Mission Cleared', 'warning');
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Save the landing bearing to database.
 *
 * @async
 * @param {string} missionID - The mission ID.
 * @param {object} newLandingBearing - A linestrnig representing the
 * landing bearing.
 */
export const setLandingBearing = async (missionID, newLandingBearing) => {
  try {
    await methodPromise('missions.setLandingBearing', missionID, newLandingBearing);
    if (!isEmpty(newLandingBearing)) {
      Bert.alert('Mission Landing Bearing Updated', 'success');
    }
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Save data from the picture grid calculations to the database.
 *
 * @async
 * @param {string} missionID - The mission ID.
 * @param {number} distanceBetweenPics - This is the distance between pics in the
 * movement direction.
 * @param {number} timeBetweenPics - This is the time between pics.
 * @param {number} groundResolution - This is the ground resolution at the
 * current settings.
 * @param {boolean} [noAlert=false] - Flag to show the confirmation alert.
 * @throws Error if something went wrong.
 */
export const setPictureGrid = async (
  missionID,
  distanceBetweenPics,
  timeBetweenPics,
  groundResolution,
  noAlert = false
) => {
  try {
    await methodPromise(
      'missions.setPictureGridData',
      missionID,
      distanceBetweenPics,
      timeBetweenPics,
      groundResolution
    );
    if (!noAlert) {
      Bert.alert('Picture Grid Data Updated', 'success');
    }
  } catch (error) {
    throw error;
  }
};
